import { Stack, SxProps } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { DEFAULT_CREATE_APPOINTMENT_DRAWER_WIDTH } from "@/components/booking/createAppointmentDrawer";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import OutlinedButton from "@/components/common/buttons/outlinedButton";
import { DropdownMenuItems } from "@/components/common/dropdownMenu/dropdownMenu";
import { useConfirm } from "@/components/providers/confirmProvider";
import TelehealthDetailsSection from "@/components/serviceFlow/appointments/telehealthDetailsSection";
import { AppointmentType, TelehealthType } from "@/types";
import { REMOVE_TELEHEALTH_DETAILS_MODAL_CONTENT } from "@/utils/telehealth";
import { AvailableService } from "./appointmentServicesSection";

type Props = {
  selectedServices: AvailableService[];
  telehealthType: TelehealthType;
  telehealthVideoLink: string;
  telehealthAdditionalDetails: string;
  onChangeTelehealthType: (type: TelehealthType) => void;
  onChangeTelehealthVideoLink: (link: string) => void;
  onChangeTelehealthAdditionalDetails: (details: string) => void;
  onChangeIsTelehealthAppointmentDetailsValid: (valid: boolean) => void;
};

export const TelehealthSection = ({
  selectedServices,
  telehealthType,
  telehealthVideoLink,
  telehealthAdditionalDetails,
  onChangeTelehealthType,
  onChangeTelehealthVideoLink,
  onChangeTelehealthAdditionalDetails,
  onChangeIsTelehealthAppointmentDetailsValid,
}: Props) => {
  const { getConfirm } = useConfirm();

  const [telehealthDetailsOpen, setTelehealthDetailsOpen] =
    useState<boolean>(false);

  const [telehealthDetailsButtonDisabled, setTelehealthDetailsButtonDisabled] =
    useState<boolean>(false);

  const closeTelehealthDetailsSection = useCallback(() => {
    setTelehealthDetailsOpen(false);
    onChangeTelehealthType(null);
    onChangeTelehealthVideoLink(null);
    onChangeTelehealthAdditionalDetails(null);
  }, [
    setTelehealthDetailsOpen,
    onChangeTelehealthType,
    onChangeTelehealthVideoLink,
    onChangeTelehealthAdditionalDetails,
  ]);

  const handleTelehealthDetailsOpen = (): void => {
    onChangeTelehealthType(TelehealthType.VIDEO);
    setTelehealthDetailsOpen(true);
  };

  const handleTelehealthDetailsRemove = async () => {
    const confirm = await getConfirm(REMOVE_TELEHEALTH_DETAILS_MODAL_CONTENT);
    if (!confirm) return;

    closeTelehealthDetailsSection();
  };

  const telehealthDropdownActions: DropdownMenuItems = [
    {
      component: "Remove telehealth details",
      onClick: handleTelehealthDetailsRemove,
    },
  ];

  useEffect(() => {
    if (
      !selectedServices?.length ||
      selectedServices?.some(
        (service) => service.appointmentType !== AppointmentType.TELEHEALTH
      )
    ) {
      closeTelehealthDetailsSection();
      setTelehealthDetailsButtonDisabled(true);
    } else {
      setTelehealthDetailsButtonDisabled(false);
    }
  }, [
    closeTelehealthDetailsSection,
    setTelehealthDetailsButtonDisabled,
    selectedServices,
  ]);

  return telehealthDetailsOpen ? (
    <Stack spacing={2}>
      <TelehealthDetailsSection
        telehealthDropdownActions={telehealthDropdownActions}
        telehealthType={telehealthType}
        telehealthVideoLink={telehealthVideoLink}
        telehealthAdditionalDetails={telehealthAdditionalDetails}
        onChangeTelehealthType={onChangeTelehealthType}
        onChangeTelehealthVideoLink={onChangeTelehealthVideoLink}
        onChangeTelehealthAdditionalDetails={
          onChangeTelehealthAdditionalDetails
        }
        onChangeIsTelehealthAppointmentDetailsValid={
          onChangeIsTelehealthAppointmentDetailsValid
        }
      />
      <TelehealthDetailsInfoBox />
    </Stack>
  ) : (
    <TelehealthDetailsSectionOpenButton
      onClick={handleTelehealthDetailsOpen}
      disabled={telehealthDetailsButtonDisabled}
    />
  );
};

const TelehealthDetailsInfoBox = ({ sx }: { sx?: SxProps }) => (
  <AdditionalInfo
    variant="info"
    text="Telehealth details will be emailed to the client 1 hour before the appointment, and will be visible in their client portal."
    sx={{
      maxWidth: DEFAULT_CREATE_APPOINTMENT_DRAWER_WIDTH,
      ...sx,
    }}
  />
);

const TelehealthDetailsSectionOpenButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => (
  <OutlinedButton
    text="Add telehealth details"
    disabled={disabled}
    withIcon={false}
    small
    onClick={onClick}
    sx={{
      width: "fit-content",
    }}
  />
);
